import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { SegmentedControl } from '@aimsos/ui';

import { PREVIEW_SIZES } from '../constants/widgetPreviewLayout.js';

/**
 * The prototype labels this control with single letters and lets the rail hug
 * them — `S M L` beside the heading, not a full-width rail of spelled-out
 * words. `SegmentedControl`'s rail is `w-full` and its segments are `flex-1`,
 * so both have to be undone; the accessible name still spells the size out,
 * which is what a screen reader announces.
 */
const RAIL_CLASS = 'w-auto shrink-0';
// `!` because these fight the variant's own `flex-1` and `px-4`, and Tailwind
// settles same-specificity conflicts by stylesheet order, not by class order.
const ITEM_CLASS = '!flex-none !px-2 min-w-[26px]';

/**
 * The `S M L` size rail, shared by the builder's live preview and the
 * catalogue's detail panel.
 *
 * One component rather than two configurations of `SegmentedControl`, because
 * two is what let them drift: the detail panel had grown a full-width rail of
 * spelled-out words with a different active colour, next to the builder's
 * letter rail, for a control that means exactly the same thing on both. The
 * widths they drive come from one place too — see `COLUMN_SPAN_BY_SIZE`.
 *
 * @param {object} props
 * @param {'small'|'medium'|'large'} props.value
 * @param {(size: string) => void} props.onChange
 */
const PreviewSizeControl = memo(function PreviewSizeControl({ value, onChange }) {
  const { t } = useTranslation('dashboards');

  const options = useMemo(
    () =>
      PREVIEW_SIZES.map((size) => ({
        value: size,
        // The letter is what the design shows; the word is what a screen reader
        // reads, since `SegmentedControl` has no per-option label prop.
        label: (
          <>
            <span aria-hidden>{t(`widgetBuilder.preview.sizeInitials.${size}`)}</span>
            <span className="sr-only">{t(`widgetLibrary.detail.size.${size}`)}</span>
          </>
        ),
      })),
    [t],
  );

  return (
    <SegmentedControl
      value={value}
      onChange={onChange}
      options={options}
      variant="buttonGroup"
      className={RAIL_CLASS}
      itemClassName={ITEM_CLASS}
      ariaLabel={t('widgetLibrary.detail.sizeAria')}
    />
  );
});

PreviewSizeControl.propTypes = {
  value: PropTypes.oneOf(['small', 'medium', 'large']).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default PreviewSizeControl;
